import * as LabelPrimitive from '@radix-ui/react-label';

import { cn } from '@veraclins-dev/utils';

import { type LabelVariants, labelVariants } from './utils/variants/label';
import { extractStyleProps } from './utils/variants/styles';

/**
 * Props for the Label component.
 * Extends Radix UI's Label.Root props with a `variant` controlling the text
 * treatment: `default` (an inline label, such as one beside a checkbox),
 * `field` (the smaller, muted label above a form field) or `caption` (an
 * uppercase eyebrow).
 */
type LabelProps = React.ComponentProps<typeof LabelPrimitive.Root> &
  LabelVariants;

/**
 * Label component for form controls and other UI elements.
 * Built on top of Radix UI's Label primitive for accessibility.
 * Includes built-in styling for disabled states and peer interactions.
 *
 * @example
 * ```tsx
 * <Label htmlFor="email">Email</Label>
 * <Input id="email" />
 * ```
 *
 * @param props - Props for the Label component
 * @returns A styled label element with accessibility features
 * @category interactive-elements
 */
function Label({ className, variant, ...props }: LabelProps) {
  const { styleProps, others } = extractStyleProps(props);

  return (
    <LabelPrimitive.Root
      data-slot="label"
      className={cn(labelVariants({ ...styleProps, variant, className }))}
      {...others}
    />
  );
}

export { Label, type LabelProps };
